import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import PropertyGalleryModal from '../../components/PropertyGalleryModal';
import PropertyInquiryModal from '../../components/PropertyInquiryModal';
import SEO from '../../components/SEO';

interface Property {
  id: string;
  title: string;
  description: string;
  price: number;
  rental_price?: number;
  listing_type: string;
  price_per_sqm?: number;
  location: string;
  type: string;
  bedrooms: number;
  bathrooms: number;
  area: number;
  features?: string[];
  image?: string;
  images?: string[];
  is_under_market?: boolean;
}

const mockProperties: Property[] = [
  {
    id: '1',
    title: 'Luksuzni stan u centru Zagreba',
    description: 'Potpuno renoviran stan s pogledom na park. Visokokvalitetna oprema, parket, klima uređaji u svim prostorijama.',
    price: 285000,
    price_per_sqm: 3800,
    listing_type: 'Prodaja',
    location: 'Donji Grad',
    type: 'Apartment',
    bedrooms: 3,
    bathrooms: 2,
    area: 75,
    features: ['Lift', 'Parking', 'Balkon', 'Klima'],
    is_under_market: false
  },
  {
    id: '2',
    title: 'Moderna kuća s vrtom',
    description: 'Novogradnja s energetskim certifikatom A+. Prostrani vrt, garaža za dva automobila, smart home sustav.',
    price: 450000,
    price_per_sqm: 2812,
    listing_type: 'Prodaja',
    location: 'Maksimir',
    type: 'House',
    bedrooms: 4,
    bathrooms: 3,
    area: 160,
    features: ['Vrt', 'Garaža', 'Smart Home', 'Terasa'],
    is_under_market: true
  },
  {
    id: '3',
    title: 'Dvosoban stan - Trnje',
    description: 'Funkcionalan stan idealan za mlade parove. Blizina tramvajske stanice i svih sadržaja.',
    price: 165000,
    price_per_sqm: 3300,
    listing_type: 'Prodaja',
    location: 'Trnje',
    type: 'Apartment',
    bedrooms: 2,
    bathrooms: 1,
    area: 50,
    features: ['Lift', 'Balkon', 'Ostava'],
    is_under_market: false
  },
  {
    id: '4',
    title: 'Penthouse s panoramskim pogledom',
    description: 'Ekskluzivni penthouse na zadnjem katu. Velika terasa, jacuzzi, premium oprema.',
    price: 520000,
    price_per_sqm: 4333,
    listing_type: 'Prodaja',
    location: 'Novi Zagreb',
    type: 'Apartment',
    bedrooms: 4,
    bathrooms: 3,
    area: 120,
    features: ['Terasa', 'Jacuzzi', 'Garaža', 'Lift', 'Klima'],
    is_under_market: false
  },
  {
    id: '5',
    title: 'Stan za najam - Centar',
    description: 'Potpuno namješten stan u srcu grada. Idealan za studente ili mlade profesionalce.',
    rental_price: 800,
    listing_type: 'Najam',
    location: 'Centar',
    type: 'Apartment',
    bedrooms: 2,
    bathrooms: 1,
    area: 55,
    features: ['Namješten', 'Klima', 'Internet'],
    is_under_market: false
  },
  {
    id: '6',
    title: 'Obiteljska kuća - Dubrava',
    description: 'Prostrana kuća s velikim vrtom. Potrebna adaptacija, odlična prilika za investiciju.',
    price: 320000,
    price_per_sqm: 2000,
    listing_type: 'Prodaja',
    location: 'Dubrava',
    type: 'House',
    bedrooms: 5,
    bathrooms: 2,
    area: 160,
    features: ['Vrt', 'Garaža', 'Podrumski prostori'],
    is_under_market: true
  }
];

export default function NekretnineListPage() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  
  const [searchFilters, setSearchFilters] = useState({
    location: '',
    minSize: '',
    maxSize: '',
    minPrice: '',
    maxPrice: ''
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [properties, activeFilter, searchFilters]);

  const fetchProperties = async () => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data && data.length > 0) {
        setProperties(data);
      } else {
        setProperties(mockProperties);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      setProperties(mockProperties);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const allProperties = properties.length > 0 ? properties : mockProperties;
    let filtered = [...allProperties];

    // Type filter
    if (activeFilter === 'apartments') {
      filtered = filtered.filter(p => p.type === 'Apartment');
    } else if (activeFilter === 'houses') {
      filtered = filtered.filter(p => p.type === 'House');
    } else if (activeFilter === 'rentals') {
      filtered = filtered.filter(p => p.listing_type === 'Najam');
    } else if (activeFilter === 'under-market') {
      filtered = filtered.filter(p => p.is_under_market === true);
    }

    // Location filter
    if (searchFilters.location.trim()) {
      const locationQuery = searchFilters.location.toLowerCase();
      filtered = filtered.filter(p => 
        p.location.toLowerCase().includes(locationQuery)
      );
    }

    // Size filter
    if (searchFilters.minSize) {
      const minSize = parseFloat(searchFilters.minSize);
      filtered = filtered.filter(p => p.area >= minSize);
    }

    if (searchFilters.maxSize) {
      const maxSize = parseFloat(searchFilters.maxSize);
      filtered = filtered.filter(p => p.area <= maxSize);
    }

    // Price filter
    if (searchFilters.minPrice) {
      const minPrice = parseFloat(searchFilters.minPrice);
      filtered = filtered.filter(p => {
        const price = p.listing_type === 'Najam' ? p.rental_price : p.price;
        return price && price >= minPrice;
      });
    }

    if (searchFilters.maxPrice) {
      const maxPrice = parseFloat(searchFilters.maxPrice);
      filtered = filtered.filter(p => {
        const price = p.listing_type === 'Najam' ? p.rental_price : p.price;
        return price && price <= maxPrice;
      });
    }

    setFilteredProperties(filtered);
  };

  const resetFilters = () => {
    setSearchFilters({
      location: '',
      minSize: '',
      maxSize: '',
      minPrice: '',
      maxPrice: ''
    });
    setActiveFilter('all');
  };

  const handlePropertyClick = (property: Property) => {
    setSelectedProperty(property);
    setShowGalleryModal(true);
  };

  const handleCloseGallery = () => {
    setShowGalleryModal(false);
  };

  const handleInquiryClick = () => {
    setShowGalleryModal(false);
    setShowInquiryModal(true);
  };

  const handleCloseInquiry = () => {
    setShowInquiryModal(false);
  };

  const handleInquirySuccess = () => {
    setShowInquiryModal(false);
  };

  const getPropertyTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      'Apartment': 'Stan',
      'House': 'Kuća',
      'Land': 'Zemljište',
      'Commercial': 'Poslovni prostor'
    };
    return labels[type] || type;
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <SEO 
        title="Nekretnine Zagreb - Prodaja Stanova i Kuća | Buy in Zagreb"
        description="Pregledajte našu ponudu provjerenih nekretnina u Zagrebu. Stanovi, kuće, poslovni prostori i zemljišta. Sigurna kupoprodaja s potpunom pravnom podrškom."
        keywords="nekretnine zagreb, stanovi zagreb, kuće zagreb, prodaja nekretnina, najam stanova zagreb"
      />

      <div className="min-h-screen bg-white text-base">
        {selectedProperty && (
          <>
            <PropertyGalleryModal
              property={selectedProperty}
              isOpen={showGalleryModal}
              onClose={handleCloseGallery}
              onInquiryClick={handleInquiryClick}
            />
            
            <PropertyInquiryModal
              property={selectedProperty}
              isOpen={showInquiryModal}
              onClose={handleCloseInquiry}
              onSuccess={handleInquirySuccess}
            />
          </>
        )}

        {/* Header */}
        <header className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl border-b border-gray-100/50 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <a href="/" className="flex-shrink-0 cursor-pointer mr-8">
                <img 
                  src="https://static.readdy.ai/image/543888419322a681fa4fe0b45060a131/83927b54a7909cd8f2d054a2d7c5ece9.png" 
                  alt="BuyInZagreb Logo" 
                  className="h-10 w-auto"
                />
              </a>
              
              <nav className="hidden lg:flex items-center space-x-6 flex-1">
                <a href="/" className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap">
                  Početna
                </a>
                <a href="/nekretnine" className="text-red-600 font-medium cursor-pointer whitespace-nowrap">
                  Nekretnine
                </a>
                <a href="/#services" className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap">
                  Usluge
                </a>
                <a href="/besplatna-procjena-nekretnine-zagreb" className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap">
                  Besplatna procjena
                </a>
                <a href="/#about" className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap">
                  O nama
                </a>
                <a href="/#blog-tools" className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap">
                  Blog
                </a>
                <a href="/#contact" className="text-gray-700 hover:text-red-600 font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap">
                  Kontakt
                </a>
              </nav>

              <div className="flex items-center space-x-3 flex-shrink-0">
                <a 
                  href="/#contact"
                  className="hidden lg:flex bg-red-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-red-700 hover:shadow-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  Kontaktirajte nas
                </a>
              </div>
            </div>
          </div>
        </header>

        <main className="pt-16">
          {/* Page Header */}
          <section className="bg-gradient-to-br from-gray-50 to-white py-12 lg:py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8">
                <h1 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4">
                  Sve nekretnine u Zagrebu
                </h1>
                <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
                  Pregledajte našu kompletnu ponudu provjerenih nekretnina
                </p>
              </div>

              {/* Filters */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Lokacija</label>
                    <input 
                      type="text"
                      value={searchFilters.location}
                      onChange={(e) => setSearchFilters({...searchFilters, location: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                      placeholder="npr. Trnje, Maksimir..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Kvadratura (m²)</label>
                    <div className="grid grid-cols-2 gap-2">
                      <input 
                        type="number"
                        value={searchFilters.minSize}
                        onChange={(e) => setSearchFilters({...searchFilters, minSize: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                        placeholder="Min"
                      />
                      <input 
                        type="number"
                        value={searchFilters.maxSize}
                        onChange={(e) => setSearchFilters({...searchFilters, maxSize: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                        placeholder="Max"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Cijena (EUR)</label>
                    <div className="grid grid-cols-2 gap-2">
                      <input 
                        type="number"
                        value={searchFilters.minPrice}
                        onChange={(e) => setSearchFilters({...searchFilters, minPrice: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                        placeholder="Min"
                      />
                      <input 
                        type="number"
                        value={searchFilters.maxPrice}
                        onChange={(e) => setSearchFilters({...searchFilters, maxPrice: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                        placeholder="Max"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {[
                    { label: 'Sve', key: 'all' },
                    { label: 'Stanovi', key: 'apartments' },
                    { label: 'Kuće', key: 'houses' },
                    { label: 'Najam', key: 'rentals' },
                    { label: 'Ispod cijene', key: 'under-market' }
                  ].map((filter) => (
                    <button 
                      key={filter.key}
                      onClick={() => setActiveFilter(filter.key)}
                      className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        activeFilter === filter.key
                          ? 'bg-red-600 text-white shadow-md' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>

                <button 
                  onClick={resetFilters}
                  className="text-red-600 hover:text-red-700 font-medium text-sm cursor-pointer"
                >
                  <i className="ri-refresh-line mr-1"></i>
                  Resetuj filtere
                </button>
              </div>
            </div>
          </section>

          {/* Properties Grid */}
          <section className="py-12 lg:py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {loading ? (
                <div className="flex justify-center items-center py-16">
                  <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
                    <div className="flex items-center space-x-3">
                      <div className="animate-spin rounded-full h-8 w-8 border-3 border-red-600/20 border-t-red-600"></div>
                      <span className="text-gray-700 text-base font-medium">Učitavanje...</span>
                    </div>
                  </div>
                </div>
              ) : filteredProperties.length === 0 ? (
                <div className="text-center py-16">
                  <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 max-w-md mx-auto">
                    <i className="ri-search-line text-4xl text-gray-400 mb-4"></i>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Nema rezultata</h3>
                    <p className="text-gray-600 mb-4">Trenutno nemamo nekretnine koje odgovaraju vašoj pretrazi.</p>
                    <button 
                      onClick={resetFilters}
                      className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors cursor-pointer"
                    >
                      Prikaži sve nekretnine
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <p className="text-gray-600">
                      Pronađeno: <strong className="text-gray-900">{filteredProperties.length}</strong> {filteredProperties.length === 1 ? 'nekretnina' : 'nekretnine'}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop="true">
                    {filteredProperties.map((property) => {
                      const isRental = property.listing_type === 'Najam';
                      const displayPrice = isRental ? property.rental_price : property.price;
                      const pricePerSqm = displayPrice && property.area 
                        ? Math.round(displayPrice / property.area) 
                        : property.price_per_sqm || 0;
                      
                      return (
                        <div 
                          key={property.id} 
                          className="group bg-white rounded-2xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-500 overflow-hidden cursor-pointer border border-gray-100"
                          onClick={() => handlePropertyClick(property)}
                        >
                          <div className="relative overflow-hidden">
                            <img 
                              src={(property.images && property.images.length > 0) ? property.images[0] : (property.image || `https://public.readdy.ai/ai/img_res/4883bbc3fff72839fd8625b98e2f0c83.jpg$%7Bproperty.type.toLowerCase%28%29%7D%20$%7Bproperty.location%7D%20Zagreb%20Croatia%2C%20professional%20real%20estate%20photography&width=800&height=600&seq=${property.id}&orientation=landscape`)}
                              alt={property.title}
                              className="w-full h-48 object-cover object-top group-hover:scale-110 transition-transform duration-700"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
                            <div className="absolute top-3 right-3 bg-red-600/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-sm font-medium shadow-lg">
                              {getPropertyTypeLabel(property.type)}
                            </div>
                            {property.is_under_market && (
                              <div className="absolute top-3 left-3 bg-green-600/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-sm font-medium shadow-lg">
                                Ispod cijene
                              </div>
                            )}
                            {isRental && (
                              <div className="absolute top-3 left-3 bg-blue-600/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-sm font-medium shadow-lg">
                                Najam
                              </div>
                            )}
                          </div>
                          
                          <div className="p-6">
                            <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-red-600 transition-colors">
                              {property.title}
                            </h3>
                            <div className="flex items-center text-gray-600 mb-4">
                              <i className="ri-map-pin-line mr-2 text-red-500"></i>
                              <span className="font-medium text-sm">{property.location}</span>
                            </div>
                            
                            <div className="flex items-center justify-between mb-3">
                              <div className="text-2xl font-bold text-red-600">
                                {displayPrice?.toLocaleString()} €{isRental ? '/mj' : ''}
                              </div>
                              <div className="text-xs text-gray-500 font-medium bg-gray-100 px-2 py-1 rounded-lg">
                                ID: {property.id}
                              </div>
                            </div>
                            
                            {!isRental && (
                              <div className="text-sm text-gray-600 mb-4 font-medium bg-red-50 px-2 py-1 rounded-lg inline-block">
                                {pricePerSqm?.toLocaleString()} €/m²
                              </div>
                            )}
                            
                            <div className="flex items-center gap-6 text-sm text-gray-700 mb-4">
                              <div className="flex items-center">
                                <i className="ri-hotel-bed-line mr-1.5 text-red-500"></i>
                                <span className="font-medium">{property.bedrooms}</span>
                              </div>
                              <div className="flex items-center">
                                <i className="ri-drop-line mr-1.5 text-red-500"></i>
                                <span className="font-medium">{property.bathrooms}</span>
                              </div>
                              <div className="flex items-center">
                                <i className="ri-ruler-line mr-1.5 text-red-500"></i>
                                <span className="font-medium">{property.area} m²</span>
                              </div>
                            </div>
                            
                            <p className="text-gray-700 text-sm line-clamp-2 mb-4 leading-relaxed">
                              {property.description}
                            </p>
                            
                            <div className="flex flex-wrap gap-1.5">
                              {property.features?.slice(0, 3).map((feature, index) => (
                                <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-lg font-medium">
                                  {feature}
                                </span>
                              ))}
                              {property.features && property.features.length > 3 && (
                                <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-lg font-medium">
                                  +{property.features.length - 3} više
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 mb-12">
              <div className="space-y-4 lg:space-y-6">
                <h3 className="text-2xl lg:text-3xl font-bold">BuyInZagreb</h3>
                <p className="text-gray-300 leading-relaxed text-sm lg:text-base">
                  Vaš partner u bez-rizičnom posredovanju od 2022.
                </p>
              </div>
              
              <div>
                <h4 className="text-base lg:text-lg font-semibold mb-4 lg:mb-6">Navigacija</h4>
                <ul className="space-y-2 lg:space-y-3">
                  <li><a href="/" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm lg:text-base">Početna</a></li>
                  <li><a href="/nekretnine" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm lg:text-base">Nekretnine</a></li>
                  <li><a href="/#services" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm lg:text-base">Usluge</a></li>
                  <li><a href="/besplatna-procjena-nekretnine-zagreb" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm lg:text-base">Besplatna procjena</a></li>
                  <li><a href="/#contact" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm lg:text-base">Kontakt</a></li>
                </ul>
              </div>
              
              <div>
                <h4 className="text-base lg:text-lg font-semibold mb-4 lg:mb-6">Pravni linkovi</h4>
                <ul className="space-y-2 lg:space-y-3">
                  <li><a href="/uvjeti-koristenja" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm lg:text-base">Uvjeti korištenja</a></li>
                  <li><a href="/privatnost-gdpr" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm lg:text-base">Politika privatnosti</a></li>
                  <li><a href="/politika-kolacica" className="text-gray-300 hover:text-white transition-colors cursor-pointer text-sm lg:text-base">Politika kolačića</a></li>
                </ul>
              </div>
            </div>
            
            <div className="border-t border-gray-700 pt-6 lg:pt-8">
              <div className="text-center text-gray-400 text-xs lg:text-sm">
                <p>© 2024 BuyInZagreb. Sva prava pridržana.</p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
